/* eslint-disable react/no-unescaped-entities */
/**
 * AboutUsPage · /about
 * ---------------------------------------------------------------------------
 * Static "who we are" page. Deep-links to the Team page (owner asked for
 * the About link to point at team).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, Users, Award, ArrowRight, Crown, Lock } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const AboutUsPage = () => {
  const { config } = useSiteConfig();
  const site = config.site || {};
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-20 sm:py-28 eh-grid-bg overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(60% 50% at 50% 35%, rgba(0,255,157,0.09), transparent 60%)' }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="eh-kicker justify-center mb-4">// ABOUT · WHO WE ARE</div>
          <h1 className="eh-display font-black leading-tight" style={{ fontSize: 'clamp(2rem, 7vw, 5rem)' }}>
            NOT AN AGENCY. <span className="eh-neon">A CREW.</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-sm sm:text-base opacity-80 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            {site.name || 'ErrorHacker'} is a small, senior team of ethical-security engineers, incident-response
            coaches, and cybersecurity educators. We don&apos;t sell hope — we sell outcomes.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/info/team" className="eh-btn-primary text-xs" data-testid="about-team-cta">
              <Users size={14} /> MEET THE TEAM <ArrowRight size={12} />
            </Link>
            <Link to="/hacking-servers" className="eh-btn-ghost text-xs">
              <Shield size={14} /> SEE OUR TOOLS
            </Link>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { i: Shield,  c: '#00ff9d', h: 'AUTHORIZED WORK ONLY', p: 'Every engagement starts with a signed scope. No unauthorized access. No malicious targets. Ever.' },
              { i: Zap,     c: '#ff2d92', h: '9 FREE AI SECURITY TOOLS',  p: 'A curated suite covering scam-check, phishing, breach exposure, mobile hardening, privacy sweep and more — no signup, no data stored.' },
              { i: Award,   c: '#ffd34d', h: 'DECADES OF SCARS',      p: 'Combined 40+ years across pentesting, red-team, OSINT, incident response, and platform escalations.' },
              { i: Lock,    c: '#4de0ff', h: 'ENCRYPTED FROM BYTE 1', p: 'End-to-end encryption on client channels. Consultation notes purged 30 days after case closure. Wallet ledger you can audit.' },
              { i: Users,   c: '#c084fc', h: 'NO CALL-CENTRES',       p: 'You talk to the person doing the work — not a script farm. One channel, one analyst, one accountable name.' },
              { i: Crown,   c: '#ff8a4d', h: 'OWNER-OPERATED',        p: 'The team owner personally reviews every consultation quote above ₹5,000 and every zero-day disclosure we ship.' },
            ].map((r, i) => {
              const I = r.i;
              return (
                <div key={i} className="eh-panel p-5 sm:p-6 relative overflow-hidden" style={{ borderColor: `${r.c}33` }}>
                  <div className="w-11 h-11 rounded-xl grid place-items-center mb-3" style={{ background: `${r.c}18`, color: r.c, border: `1px solid ${r.c}45` }}>
                    <I size={20} />
                  </div>
                  <div className="eh-mono text-[11px] tracking-widest font-bold mb-1.5" style={{ color: r.c }}>{r.h}</div>
                  <div className="text-[13px] opacity-80 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>{r.p}</div>
                  <div className="absolute inset-x-0 bottom-0 h-[2px] opacity-60" style={{ background: `linear-gradient(90deg, transparent, ${r.c}, transparent)` }} />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 sm:py-20 border-t border-[var(--eh-border)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="eh-kicker mb-3">// STORY</div>
          <h2 className="eh-display font-black mb-6" style={{ fontSize: 'clamp(1.6rem, 4.5vw, 2.8rem)' }}>WHY WE STARTED THIS.</h2>
          <div className="space-y-4 text-[15px] leading-relaxed opacity-90" style={{ fontFamily: 'Inter,sans-serif' }}>
            <p>
              A friend lost a 200k-follower Instagram account overnight — reported by a competitor for a policy she never broke.
              Meta support was a black hole. Every &quot;recovery agency&quot; wanted ₹40k upfront and delivered nothing.
              After 11 days, we coached her through Instagram&apos;s official appeal flow — and her account came back for free.
            </p>
            <p>
              That&apos;s when we realised the market for account recovery help, ethical security tooling, and honest defence work
              was owned by scammers with three-page websites. So we built <span className="eh-neon font-bold">{site.name || 'ErrorHacker'}</span> —
              a transparent studio where consultation is <em>money-back if we can&apos;t help</em>, AI security tools are <em>free-tier first</em>,
              and every service surfaces the analyst&apos;s real callsign next to the receipt.
            </p>
            <p>
              We&apos;ve since coached 2,300+ clients across 40 countries. Zero data breaches, zero unauthorized ops,
              zero unpaid refunds. That&apos;s the bar we hold.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="py-14 sm:py-18">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="eh-panel p-6 sm:p-8" style={{ borderColor: 'rgba(0,255,157,.35)' }}>
            <div className="eh-mono text-[11px] tracking-widest opacity-70 mb-2">// COMPLIANCE</div>
            <div className="text-lg sm:text-xl font-bold mb-3" style={{ fontFamily: 'Inter,sans-serif' }}>What we <span className="eh-neon">never</span> do</div>
            <ul className="space-y-2 text-[13.5px] opacity-90 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
              {[
                'No unauthorised access to third-party accounts',
                'No stalkerware, no doxing, no revenge campaigns',
                'No credential stuffing or spam networks',
                'No SIM swaps, no synthetic identities, no fraud',
                'No engagement botnets we haven\'t audited for TOS compliance',
              ].map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-[var(--eh-green)] mt-1">✓</span> {s}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/terms" className="eh-btn-ghost text-xs">READ TERMS</Link>
              <Link to="/privacy" className="eh-btn-ghost text-xs">PRIVACY POLICY</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-14 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="eh-panel eh-brackets text-center p-8 sm:p-12">
            <span className="br-bl" /><span className="br-br" />
            <div className="eh-kicker justify-center mb-3">// TALK TO A HUMAN</div>
            <h3 className="eh-display font-black mb-4" style={{ fontSize: 'clamp(1.6rem, 4.5vw, 2.8rem)' }}>
              Meet the crew. <span className="eh-neon">Then decide.</span>
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/info/team" className="eh-btn-primary text-xs" data-testid="about-team-cta-bottom">
                <Crown size={14} /> VIEW THE TEAM <ArrowRight size={12} />
              </Link>
              {site.telegram && (
                <a href={site.telegram} className="eh-btn-ghost text-xs" target="_blank" rel="noreferrer">
                  DM ON TELEGRAM
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;
